import { SelectProps } from 'antd';
import config from 'config';
import {
  OwnerType,
  useGetUsersListLazyQuery,
  useGetUsersListQuery,
  UserRole,
} from 'gql/types';
import { Select } from 'modules/common/components';
import styles from 'modules/common/components/Select/styles.module.scss';
import { useDebounce } from 'modules/common/hooks';
import { getMissingValuesFromList } from 'modules/common/utils/getMissingValuesFromList';
import { getCachedUsers } from 'modules/user/utils/user-cache';
import { ComponentProps, FC, useEffect, useMemo, useState } from 'react';
import { useController, UseControllerProps } from 'react-hook-form';

type PropTypes = {
  userRoles?: UserRole[];
  ownerTypes?: OwnerType[];
  isArchived?: boolean;
  controlled: UseControllerProps<any>;
  productRegionPreferenceId?: string;
} & Omit<ComponentProps<typeof Select>, 'options'>;

const UserSelect: FC<PropTypes> = ({
  userRoles,
  ownerTypes,
  controlled,
  className,
  isArchived,
  productRegionPreferenceId,
  ...restProps
}) => {
  const classNames = [styles.select, className || ''].join(' ');
  const isOffline = !navigator.onLine;
  const controller = useController(controlled);
  const { clearDebounce, debounce } = useDebounce();
  const [getUsersByIds, getUsersByIdsQuery] = useGetUsersListLazyQuery();
  const getUsersListQuery = useGetUsersListQuery({
    skip: isOffline,
    variables: {
      input: {
        filter: {
          roles: userRoles,
          ownerTypes,
          isArchived,
          productRegionPreferenceId,
        },
        take: config.PAGINATION.USER_SELECT_TAKE,
      },
    },
    fetchPolicy: 'cache-first',
  });

  const cachedUsers = useMemo(() => Object.values(getCachedUsers()), []);

  const [localSearch, setLocalSearch] = useState<string>('');
  const offlineFiltered = isOffline
    ? cachedUsers.filter((u) =>
        `${u.firstName} ${u.lastName}`
          .toLowerCase()
          .includes(localSearch.toLowerCase())
      )
    : [];

  const fetchedUsers = [
    ...(isOffline
      ? offlineFiltered
      : getUsersListQuery.data?.getUsersList.data || []),
    ...(getUsersByIdsQuery.data?.getUsersList.data || []),
  ];

  const userMap = new Map(fetchedUsers.map((u) => [u.id, u]));
  const usersUniq = Array.from(userMap.values());

  const userList: SelectProps['options'] = useMemo(() => {
    const sortedUsers = usersUniq.sort((a, b) => {
      const nameA = `${a.firstName} ${a.lastName}`.toLowerCase();
      const nameB = `${b.firstName} ${b.lastName}`.toLowerCase();
      return nameA.localeCompare(nameB);
    });

    return sortedUsers.map((user) => ({
      value: user.id,
      label: (
        <>
          {user.firstName} <b>{user.lastName}</b>
        </>
      ),
    }));
  }, [usersUniq]);

  useEffect(() => {
    const currentValue = controller.field.value;
    if (!currentValue) {
      return;
    }

    const missingSelectedOptions = getMissingValuesFromList(
      currentValue,
      userList
    );

    if (!missingSelectedOptions?.length || isOffline) {
      return;
    }

    getUsersByIds({
      variables: {
        input: {
          filter: {
            ids: missingSelectedOptions,
          },
        },
      },
    });
  }, [controller.field.value, userList, isOffline]);

  const handleSearch = (value: string) => {
    if (isOffline) {
      setLocalSearch(value);
      return;
    }

    getUsersListQuery.refetch({
      input: {
        filter: {
          username: value,
          roles: userRoles,
          isArchived,
        },
      },
    });
  };

  useEffect(() => clearDebounce, []);

  const hasMissingSelectedOptions =
    getMissingValuesFromList(controller.field.value, userList).length > 0;

  const value = hasMissingSelectedOptions ? null : controller.field.value;

  return (
    <Select
      options={userList}
      value={value}
      onChange={(val) => controller.field.onChange(val)}
      onBlur={controller.field.onBlur}
      {...restProps}
      className={classNames}
      showSearch
      adjustDropdownPosition
      showArrow
      loading={
        !isOffline && (getUsersListQuery.loading || getUsersByIdsQuery.loading)
      }
      filterOption={false}
      onSearch={debounce(handleSearch)}
      defaultActiveFirstOption={false}
    />
  );
};

export default UserSelect;
